import {MixModel, ProjectDataIndex} from '../../api/types.ts';
import React, {useEffect, useState} from 'react';
import {applySplit, disposeSplit} from '../../lib/apply_split.js';
import emptyGuid from '../../lib/emptyGuid.ts';
import VibeCodeTools from './VibeCodeTools.tsx';
import SceneFrame from '../scene/frame/SceneFrame.tsx';
import FocusModeExitControl from '../shared/FocusModeExitControl.tsx';
import useFillViewportHeight from '../../hooks/useFillViewportHeight.ts';
import VibeGameActions from './VibeGameActions.tsx';
import {isRenderMode} from '../../lib/tabView.ts';

interface VibeCodeComponentProps {
    project: MixModel;
    dataIndex: ProjectDataIndex;
    readOnly: boolean;
    isFocusMode?: boolean;
    onExitFocusMode?: () => void;
    autoOpenVibe?: boolean;
    // With autoOpenVibe on a copy: translate it into this language.
    autoVibeLanguage?: string | null;
    onVibeAutoOpened?: () => void;
}

const VibeCodeComponent: React.FC<VibeCodeComponentProps> = ({
    project,
    dataIndex,
    readOnly,
    isFocusMode = false,
    onExitFocusMode = () => undefined,
    autoOpenVibe,
    autoVibeLanguage,
    onVibeAutoOpened
}) => {
    const {id} = project;
    const projectId = id!;
    const [syncToken, setSyncToken] = useState<string>(emptyGuid);
    const {ref: splitRef, height: fillHeight} = useFillViewportHeight();
    const splitInstanceId = 'MixSceneSplit';

    useEffect(() => {
        !readOnly &&
            applySplit(splitInstanceId, ['#MixSceneTools', '#MixScene'], {
                sizes: [25, 75]
            });

        return () => {
            !readOnly && disposeSplit(splitInstanceId);
        };
    }, []);

    if (readOnly) {
        return (
            <div
                className={`mixcode-code vibe-code-surface focus-mode-surface${
                    isFocusMode ? ' focus-mode-surface--active' : ''
                }`}
                id="MixScene"
            >
                {isFocusMode && (
                    <FocusModeExitControl onExit={onExitFocusMode} />
                )}
                {/* The render page takes pictures of the game alone. */}
                {!isRenderMode() && (
                    <VibeGameActions project={project} dataIndex={dataIndex} />
                )}
                <SceneFrame
                    id="MixSceneIframe"
                    style={{
                        width: '100%',
                        height: '100%',
                        minHeight: '800px',
                        overflow: 'auto'
                    }}
                    projectId={projectId}
                    dataIndex={dataIndex}
                    syncToken={syncToken}
                    tabIndex={0}
                />
            </div>
        );
    }

    return (
        <div
            ref={splitRef}
            className={`split overflow-hidden vibe-code-surface focus-mode-surface${
                isFocusMode ? ' focus-mode-surface--active' : ''
            }`}
            style={{
                height: fillHeight === null ? '100vh' : `${fillHeight}px`
            }}
        >
            {isFocusMode && (
                <FocusModeExitControl
                    onExit={onExitFocusMode}
                    label="Back to editor"
                />
            )}
            <div
                className="mixcode-tools custom-scrollbar overflow-y-auto"
                id="MixSceneTools"
            >
                <VibeCodeTools
                    project={project}
                    dataIndex={dataIndex}
                    setSyncToken={setSyncToken}
                    autoOpenVibe={autoOpenVibe}
                    autoVibeLanguage={autoVibeLanguage}
                    onVibeAutoOpened={onVibeAutoOpened}
                />
            </div>
            <div className="mixcode-code" id="MixScene">
                <SceneFrame
                    id="MixSceneIframe"
                    style={{
                        width: '100%',
                        height: '100%',
                        minHeight: 'auto',
                        maxHeight: '100%',
                        overflow: 'auto'
                    }}
                    projectId={projectId}
                    dataIndex={dataIndex}
                    syncToken={syncToken}
                />
            </div>
        </div>
    );
};

export default VibeCodeComponent;
